import { pipeChainCardCarousel } from "../blocks/pipechain_card_carousel";
import { cardGridLinks } from "../blocks/pipechain_card_grid";
import { foldOutCards } from "../blocks/pipechain_fold_out_cards";
import { pipeChainLogos } from "../blocks/pipechain_logos";
import "../css/main";
import htmx from "htmx.org/dist/htmx";
import { syncHeadlines } from "./sync-headlines";

const hashOnLoad = window.location.hash;
// Prevent scroll to hash hack!
if (hashOnLoad) {
  history.replaceState(undefined, "", "#hash_value");
  // Set the openTab query param and use that instead of the hash...
  const params = new URLSearchParams(window.location.search);
  params.set("openTab", hashOnLoad.slice(1));
  var newurl =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname +
    "?" +
    params.toString();
  window.history.replaceState({ path: newurl }, "", newurl);
}

// Add tab support for the native buttons.
function buttonTabs(afterFn: () => void) {
  // We need to group the buttons into their own groups...
  document.querySelectorAll(".wp-block-buttons").forEach((el) => {
    // Make sure this group even has a valid link...
    if (!el.querySelector('.wp-block-button a[href^="#tab-"]')) return;

    // This holds the state for this group...
    let state: {
      tabs: Element[];
      buttons: Element[];
    } = {
      tabs: [],
      buttons: [],
    };

    function changeActiveTab(index: number) {
      state.tabs.forEach((el) => el.classList.add("hidden"));
      state.tabs[index].classList.remove("hidden");

      // A little fade in effect...
      state.tabs[index].classList.add("transition-opacity");
      state.tabs[index].classList.add("duration-300");
      state.tabs[index].classList.add("opacity-0");
      setTimeout(() => {
        state.tabs[index].classList.add("opacity-100");
        state.tabs[index].classList.remove("opacity-0");
        state.tabs[index].classList.remove("opacity-100");
        setTimeout(() => {
          state.tabs[index].classList.remove("transition-opacity");
        }, 300);
      }, 5);

      state.buttons.forEach((el) => {
        el.closest("div")?.classList.remove("is-style-fill");
        el.closest("div")?.classList.add("is-style-outline");
      });
      state.buttons[index].closest("div")?.classList.remove("is-style-outline");
      state.buttons[index].closest("div")?.classList.add("is-style-fill");
      afterFn();
    }

    el.querySelectorAll('.wp-block-button a[href^="#tab-"]').forEach(
      (el, i) => {
        // Populate the tabs in the state array...
        const id = el.getAttribute("href");
        if (!id) return;
        const tabEl = document.querySelector(id);
        if (!tabEl) return;
        state.tabs.push(tabEl);
        state.buttons.push(el);
        // Now, on click toggle to the button to change the active content.
        el.addEventListener("click", (e) => {
          e.preventDefault();
          // ...prevent default but do add the hash to the url!
          const scrollY = window.scrollY;
          window.location.hash = id;
          window.scrollTo(0, scrollY);
          changeActiveTab(i);
        });
      },
    );

    // If we have a hash tied to the URL, go to that one!
    const params = new URLSearchParams(window.location.search);
    if (params.get("openTab")) {
      const index = state.tabs.findIndex(
        (el) => el.id === params.get("openTab"),
      );
      if (index > -1) changeActiveTab(index);
    }
    // Otherwise, go to the first one...
    else changeActiveTab(0);
  });
}

// Add HTMX func to WP menus...
function htmxMenu() {
  const links = document.querySelectorAll(
    ".menu li > a",
  ) as NodeListOf<HTMLAnchorElement>;
  links.forEach((el) => {
    const href = el.href;
    // HTMX attributes
    el.setAttribute("hx-get", href);
    el.setAttribute("hx-trigger", "click");
    el.setAttribute("hx-target", "main");
    el.setAttribute("hx-swap", "outerHTML show:window:top");
    el.setAttribute("hx-push-url", "true");
    el.setAttribute("hx-headers", '{"HX-Only-Load-Main": "true"}');

    // Add styling to active class...
    el.addEventListener("click", () => {
      // Fade out content...
      const mainContentEl = document.getElementById("pc_main_content__page");
      mainContentEl && mainContentEl.classList.add("opacity-100");
      mainContentEl && mainContentEl.classList.add("transition-opacity");
      mainContentEl && mainContentEl.classList.add("duration-75");
      mainContentEl && mainContentEl.classList.add("opacity-0");

      links.forEach((el) => {
        const li = el.closest("li");
        li?.classList.remove("current-menu-item");
        li?.classList.remove("current_page_item");
        if (el.href === href) li?.classList.add("current-menu-item");
        li?.classList.add("current_page_item");
      });
      // Hide mega menu on click!
      const megaToggle = document.querySelector(
        "#mega-menu-toggle",
      ) as HTMLInputElement;
      megaToggle.checked = false;
    });
  });
  htmx.findAll(".menu").forEach((menu) => htmx.process(menu));
}

// Add arrow css to certain links...
function arrowLinks() {
  document
    .querySelectorAll(".wp-block-column > p, .single p, .single h4")
    .forEach((el) => {
      el.querySelectorAll("a").forEach((anchor) => {
        if (anchor.closest("footer")) return;
        if (anchor.textContent === el.textContent) {
          anchor.classList.add("link-with-arrow");
        }
      });
    });
}

// SVG arrows to buttons
function arrowButtons() {
  const arrow = `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22.461"
      height="14.584"
      viewBox="0 0 22.461 14.584"
      style="
        position: absolute;
        top: 50%;
        right: 1.4rem;
        transform: translateY(-50%);
        color: currentColor;
      "
    >
      <g transform="translate(-1215.5 -786.211)">
        <line x2="19.433" transform="translate(1216.5 793.503)" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"/>
        <path d="M1283,801.435l5.92-5.918L1283,789.68" transform="translate(-51.959 -2.055)" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
      </g>
    </svg>
  `;
  document.querySelectorAll(".wp-element-button").forEach((el) => {
    if (!el.classList.contains("wp-block-button__link--no-arrow")) {
      el.innerHTML += arrow;
    }
  });
}

export function carousel(
  el: Element,
  slides: NodeListOf<HTMLElement>,
  _simSlides: [number, number, number, number] = [2, 2, 3, 4],
  onChange?: (idx: number, simSlides: number) => void,
  autoPlay?: boolean,
) {
  // how many to show at the same time
  const resizeThresholds = [0, 768, 1024, 1280];
  let currentResizeThreshold = 0;
  // Set current resize threshold
  resizeThresholds.forEach((threshold, i) => {
    if (window.innerWidth > threshold) currentResizeThreshold = i;
  });

  let simSlides = _simSlides[currentResizeThreshold];

  // simSlides needs to be responsive!
  window.addEventListener(
    "resize",
    () => {
      resizeThresholds.forEach((threshold, i) => {
        if (window.innerWidth > threshold) currentResizeThreshold = i;
      });
      if (simSlides === _simSlides[currentResizeThreshold]) return;
      simSlides = _simSlides[currentResizeThreshold];
      setActiveSlide(state.activeSlide, "none");
    },
    { passive: true },
  );

  // next/prev buttons
  const prevButton = el.querySelector(".pipechain-carousel__prev-button");
  const nextButton = el.querySelector(".pipechain-carousel__next-button");

  // state, maybe overkill
  let state = {
    activeSlide: 0,
    activeEl: slides[0],
    // During fades etc...
    disabled: false,
    autoPlay: 0,
  };

  // This function is for making all elements the same height...
  // TODO: Make responsive!
  function updateHeight() {
    let minHeight = 0;
    slides.forEach((el) => {
      el.style.opacity = "0";
      el.style.display = "block";
      setTimeout(() => {
        minHeight = Math.max(minHeight, el.offsetHeight);
        el.style.opacity = "";
        el.style.display = "";
      }, 1);
    });
    setTimeout(() => {
      slides.forEach((el) => {
        el.style.minHeight = `${minHeight}px`;
      });
    }, 10);
  }
  updateHeight();

  function fadeInElement(el: HTMLElement) {
    el.classList.add("pipechain-carousel__card--opacity-0");
    el.classList.add("pipechain-carousel__card--fade-transition");
    state.disabled = true;
    setTimeout(() => {
      el.classList.add("pipechain-carousel__card--opacity-100");
      el.classList.remove("pipechain-carousel__card--opacity-0");
      setTimeout(() => {
        el.classList.remove("pipechain-carousel__card--fade-transition");
        el.classList.remove("pipechain-carousel__card--opacity-100");
        state.disabled = false;
      }, 295); // Make sure this mathces the css transition...
    }, 5);
  }

  async function setActiveSlide(
    idx: number,
    direction: "left" | "right" | "none",
  ) {
    if (state.disabled) return;
    if (!state.activeEl) throw new Error("No active element");
    state.activeEl.classList.remove("pipechain-carousel__card--first-active");
    state.activeSlide = idx;
    state.activeEl = slides[idx];
    if (!state.activeEl) throw new Error("No active element");
    state.activeEl.classList.add("pipechain-carousel__card--first-active");

    onChange && onChange(idx, simSlides);

    onIdxChange(() => {
      if (direction === "left") fadeInElement(state.activeEl);
      if (direction === "right") {
        let nextEl: Element | undefined | null = state.activeEl;
        for (let i = 0; i < simSlides - 1; i++)
          nextEl = nextEl?.nextElementSibling;
        nextEl && fadeInElement(nextEl as HTMLElement);
      }
    });
  }

  function onIdxChange(after?: () => void) {
    // Possibly "disable" buttons
    const prevDisabled = state.activeSlide === 0;
    const nextDisabled =
      state.activeSlide === slides.length - simSlides ||
      slides.length < simSlides;

    prevButton?.classList.toggle(
      "pipechain-carousel__inactive-button",
      prevDisabled,
    );
    if (prevDisabled) {
      prevButton?.setAttribute("disabled", "");
    } else prevButton?.removeAttribute("disabled");
    nextButton?.classList.toggle(
      "pipechain-carousel__inactive-button",
      nextDisabled,
    );
    if (nextDisabled) {
      nextButton?.setAttribute("disabled", "");
    } else nextButton?.removeAttribute("disabled");

    // Fade in all elements..
    let currentEl: HTMLElement | null = slides[0];
    let activeIndex = -1;
    for (let i = 0; i < slides.length; i++) {
      if (!currentEl) break;
      if (currentEl === state.activeEl) activeIndex = i;
      currentEl.classList.toggle(
        "pipechain-carousel__card--visible",
        activeIndex > -1 && i < simSlides + activeIndex,
      );
      currentEl = currentEl.nextElementSibling as HTMLElement;
    }
    // We can pass a function here and execute after all
    // items are visible
    after && after();
  }

  nextButton?.addEventListener("click", () => {
    if (state.disabled) return;
    state.autoPlay && clearInterval(state.autoPlay);
    state.activeSlide < slides.length - simSlides &&
      setActiveSlide(state.activeSlide + 1, "right");
  });

  prevButton?.addEventListener("click", () => {
    if (state.disabled) return;
    state.autoPlay && clearInterval(state.autoPlay);
    state.activeSlide > 0 && setActiveSlide(state.activeSlide - 1, "left");
  });

  // init
  state.activeEl && setActiveSlide(0, "none");

  if (autoPlay) {
    state.autoPlay = setInterval(() => {
      if (state.disabled) return;
      if (state.activeSlide < slides.length - simSlides)
        setActiveSlide(state.activeSlide + 1, "right");
      else setActiveSlide(0, "left");
    }, 3_000);
  }
}

function addColumnsMeta() {
  document.querySelectorAll(".wp-block-columns").forEach((el) => {
    const columns = el.querySelectorAll(".wp-block-column");
    columns.forEach((col) => {
      col.classList.add("wp-block-column--" + columns.length + "-cols");
    });
  });
}

function mainMenuLangSwitcher() {
  // Style them etc.
  const wrappers = document.querySelectorAll(".menu-main-nav-container .menu");
  for (const wrapper of wrappers) {
    const items = wrapper.querySelectorAll(".weglot-language");
    // Wrap all items in a span
    const span = document.createElement("span");
    span.classList.add("pipechain-lang-switcher");
    items.forEach((el) => span.appendChild(el));
    // Add to the menu
    wrapper?.appendChild(span);
  }
}

function spaceBetweenColumnsWithImageLeft() {
  const columnWrappers = document.querySelectorAll(
    ".wp-block-columns",
  ) as NodeListOf<HTMLDivElement>;
  columnWrappers.forEach((el) => {
    const hasSingleImage = el.querySelector(
      ".wp-block-column > .wp-block-image:only-child",
    );
    if (!hasSingleImage) return;
    const cols = el.querySelectorAll(".wp-block-column");
    if (cols.length !== 2) return;
    el.classList.add("wp-block-columns__extra-gap");
  });
}

function main() {
  // htmxMenu();
  arrowButtons();
  pipeChainLogos();
  pipeChainCardCarousel();
  cardGridLinks();
  foldOutCards();
  arrowLinks();
  buttonTabs(() => {
    setTimeout(() => {
      foldOutCards();
    }, 0);
  });
  addColumnsMeta();
  mainMenuLangSwitcher();
  spaceBetweenColumnsWithImageLeft();

  syncHeadlines();

  window.addEventListener(
    "resize",
    () => {
      foldOutCards();
      syncHeadlines();
    },
    { passive: true },
  );
}

// Run on swap/load!
document.addEventListener("DOMContentLoaded", () => main());
document.addEventListener("htmx:afterSwap", () => main());
